/**
 * SiteChangeCard — #60 / DEC-365: a driver's pickup-site change the server parked, in words.
 *
 * The phone always queues a site change and the server applies it only while the job is still
 * at the site the phone saw. When it could not (the office changed the site meanwhile, the
 * ticket was approved, the site was retired…), the change waits here: where the driver thought
 * the job was, where they moved it, where it is now, their reason, and why it was refused.
 */

import React from 'react';
import type { SiteChangeParkedSite, SiteChangeParkedView } from '@lodex-pro/shared';
import { JOB_SITE_CHANGE_REASON_LABELS } from '@lodex-pro/shared/jobs/site-change';
import { formatTimestamp } from '../../lib/timestamp';

/** Why the server refused it, in words a reviewer reads. */
export const SITE_CHANGE_REFUSAL_TEXT: Readonly<Record<string, string>> = {
  SITE_CHANGED_ELSEWHERE: 'The job’s site had already been changed by someone else.',
  SITE_CHANGE_LOCKED:
    'The ticket had already been approved (or the driver changed it after Complete).',
  SITE_CUSTOMER_MISMATCH: 'The site belongs to another customer.',
  SITE_NOT_PICKABLE: 'The site could not be picked up from (retired, or drop-off only).',
  SOURCE_WELL_NOT_LINKED: 'The well is not linked to that location.',
};

function siteText(s: SiteChangeParkedSite | null): string {
  if (s === null) return 'No site';
  return s.label ?? 'A site that no longer exists';
}

function reasonText(view: SiteChangeParkedView): string {
  const label =
    view.reasonCode !== null && view.reasonCode in JOB_SITE_CHANGE_REASON_LABELS
      ? JOB_SITE_CHANGE_REASON_LABELS[view.reasonCode as keyof typeof JOB_SITE_CHANGE_REASON_LABELS]
      : null;
  const parts = [label !== null && view.reasonCode !== 'OTHER' ? label : null, view.reason];
  return parts.filter((p): p is string => p !== null && p !== '').join(' — ') || '—';
}

const ROW: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '150px 1fr',
  gap: '6px',
  fontSize: '13px',
  padding: '3px 0',
};
const KEY: React.CSSProperties = { color: 'var(--color-text-tertiary)' };

export function SiteChangeCard({
  view,
  timeZone,
}: {
  view: SiteChangeParkedView;
  timeZone: string;
}): React.JSX.Element {
  const movedSince =
    view.siteAtPark !== null &&
    view.siteNow !== null &&
    (view.siteAtPark.id !== view.siteNow.id ||
      view.siteAtPark.sourceWellId !== view.siteNow.sourceWellId);
  return (
    <section data-testid="site-change-card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={ROW}>
        <span style={KEY}>Driver moved it from</span>
        <span>{siteText(view.fromSite)}</span>
      </div>
      <div style={ROW}>
        <span style={KEY}>To</span>
        <strong>{siteText(view.toSite)}</strong>
      </div>
      <div style={ROW}>
        <span style={KEY}>Job is at now</span>
        <span>
          {siteText(view.siteNow)}
          {movedSince && ` (it was at ${siteText(view.siteAtPark)} when this was parked)`}
        </span>
      </div>
      <div style={ROW}>
        <span style={KEY}>Driver’s reason</span>
        <span>{reasonText(view)}</span>
      </div>
      {view.occurredAt !== null && (
        <div style={ROW}>
          <span style={KEY}>Changed on the phone</span>
          <span>{formatTimestamp(view.occurredAt, timeZone)}</span>
        </div>
      )}
      <div style={ROW}>
        <span style={KEY}>Why it waited</span>
        <span>
          {view.refusal === null
            ? '—'
            : (SITE_CHANGE_REFUSAL_TEXT[view.refusal] ?? view.refusal.replaceAll('_', ' '))}
        </span>
      </div>
    </section>
  );
}
